/**
 * The language picker — one widget, two hosts.
 *
 * The landing bar puts a `<div data-slot="lang-pick">` beside the sign-in controls and the
 * mobile drawer puts a `<div data-slot="lang-pick-mobile">` in its stack; `compact: true` is
 * the drawer form (full-width row, menu opens in flow below it instead of floating over a
 * page that is already scrolled). Same code both times, because two pickers is two places to
 * forget a locale.
 *
 * Built as a listbox rather than a `<select>`: the shell's controls are painted plates (the
 * `plate-key` button, a `kit-window` of `kit-well` rows) and a native select cannot wear them. That
 * trade costs the keyboard behaviour a select gives for free, so it is implemented here in
 * full — Enter/Space to open, arrows and Home/End to move, Enter/Space to choose, Escape to
 * close and hand focus back to the button, a tap outside to dismiss. Every control clears the
 * 44x44 minimum, and motion is `motion-safe:` only, so `prefers-reduced-motion` gets a picker
 * that snaps rather than one that animates.
 *
 * It owns no locale state: it reads `currentLocale()`, calls `setLocale()` and repaints from
 * `subscribe()` — so it stays right when something else changes the language.
 */
import { icon, plateChrome, plateContent } from '../shell/ui';
import { LOCALES, currentLocale, setLocale, subscribe, t, type LocaleId } from './index';

export interface LangPickOptions {
  /** Drawer form: full width, menu in flow, language names spelled out. */
  compact?: boolean;
  /** Top-bar form: the menu hangs from the right edge, because the picker sits at the right end of the bar. */
  bare?: boolean;
}

/** Tap-target floor on the key and every option; motion is `motion-safe:` only. */
const KEY_BASE = 'min-h-[44px] font-heading uppercase tracking-[0.14em] text-[12px] [touch-action:manipulation]';

const OPTION_BASE =
  'kit-well flex items-center justify-between gap-3 w-full min-h-[44px] px-3 text-left '
  + 'font-heading uppercase tracking-[0.12em] text-[12px] cursor-pointer '
  + '[touch-action:manipulation] motion-safe:transition-[filter] motion-safe:duration-100 hover:brightness-125';

/** The kit's own arrow, turned down. */
function caret(): HTMLElement {
  return icon('next', { size: 18, className: 'rotate-90' });
}

export function mountLangPick(host: HTMLElement, opts: LangPickOptions = {}): () => void {
  const compact = opts.compact === true;
  const bare = opts.bare === true;

  const root = document.createElement('div');
  // `inline-flex` on the floating form, and it is load-bearing: the menu is positioned against
  // THIS element, so a block-level root takes its host's full width and `right-0` then means
  // "the right edge of whatever card this sits in". In Settings that is a card the width of half
  // the page, and the menu opened in its bottom-right corner, a hand's width from the button
  // that owns it (reported 2026-09-18). Hugging the button is what makes `top-100%` and the
  // edge alignment below mean what they say.
  root.className = compact ? 'relative w-full' : 'relative inline-flex';
  // `data-lang-pick`, not `data-slot` — see the note in `sound-toggle.ts`: the bar's seat owns
  // the slot name, and duplicating it here made `[data-slot="lang-pick"]` ambiguous.
  root.dataset.langPick = 'root';

  const button = document.createElement('button');
  button.type = 'button';
  // The painted steel key every other button is; `bare` (the landing bar) wears the same key —
  // a text-only control beside painted CTAs was the flat one in the row.
  plateChrome(button, '', { variant: 'secondary', className: `${KEY_BASE} ${compact ? 'w-full' : 'px-3'}` });
  button.setAttribute('aria-haspopup', 'listbox');
  button.setAttribute('aria-expanded', 'false');
  button.setAttribute('aria-label', t('common.chooseLanguage'));

  const label = document.createElement('span');
  label.dataset.slot = 'lang-pick-label';
  plateContent(button).append(label, caret());

  const menu = document.createElement('div');
  menu.setAttribute('role', 'listbox');
  menu.setAttribute('aria-label', t('common.language'));
  menu.hidden = true;
  // Which edge it hangs from follows where the button lives. `bare` is the landing bar, where
  // the picker sits at the right end of a row and a menu growing rightwards would leave the
  // page; everywhere else the button is at the LEFT of a form row, and a right-aligned menu
  // hung 126px off the card's left edge instead of under the control.
  menu.className = compact
    ? 'kit-window [--kw-rim:16px] [--kw-pad:8px] mt-2 w-full flex flex-col gap-1.5'
    : 'kit-window [--kw-rim:16px] [--kw-pad:8px] absolute top-[calc(100%+6px)] '
      + (bare ? 'right-0 ' : 'left-0 ')
      + 'z-40 min-w-[210px] flex flex-col gap-1.5';

  const options = LOCALES.map((locale) => {
    const option = document.createElement('div');
    option.setAttribute('role', 'option');
    option.tabIndex = -1;
    option.dataset.locale = locale.id;
    option.className = OPTION_BASE;

    const name = document.createElement('span');
    name.textContent = locale.label;
    // The locale code stands in for a flag: a flag is a country, English is spoken in more than
    // one, and an emoji flag does not draw on Windows.
    const code = document.createElement('span');
    code.className = 'kit-chip [--kh:26px] kit-label font-heading text-[11px] tabular-nums';
    code.textContent = locale.short;
    option.append(name, code);
    menu.append(option);
    return option;
  });

  root.append(button, menu);
  host.replaceChildren(root);

  // ── State ───────────────────────────────────────────────────────────────────────────────
  let open = false;

  const paint = (): void => {
    const current = currentLocale();
    const meta = LOCALES.find((l) => l.id === current) ?? LOCALES[0];
    // The bar form shows the code (space is at a premium beside two CTAs); the drawer form has
    // a whole row and shows the language's own name.
    label.textContent = compact ? meta.label : meta.short;
    button.title = meta.label;
    for (const option of options) {
      const selected = option.dataset.locale === current;
      option.setAttribute('aria-selected', selected ? 'true' : 'false');
      option.className = `${OPTION_BASE} ${selected ? 'kit-selected kit-label' : 'text-text-dim hover:text-white'}`;
    }
  };

  const activeIndex = (): number => {
    const focused = options.findIndex((o) => o === document.activeElement);
    if (focused >= 0) return focused;
    const selected = options.findIndex((o) => o.dataset.locale === currentLocale());
    return selected >= 0 ? selected : 0;
  };

  const focusOption = (index: number): void => {
    const bounded = (index + options.length) % options.length;
    options[bounded]?.focus();
  };

  const setOpen = (next: boolean, focusButton = false): void => {
    open = next;
    menu.hidden = !next;
    button.setAttribute('aria-expanded', next ? 'true' : 'false');
    if (next) focusOption(activeIndex());
    else if (focusButton) button.focus();
  };

  const choose = (id: LocaleId): void => {
    setOpen(false, true);
    if (id === currentLocale()) return;
    void setLocale(id);
  };

  // ── Wiring ──────────────────────────────────────────────────────────────────────────────
  const onButtonClick = (): void => { setOpen(!open); };

  const onButtonKey = (event: KeyboardEvent): void => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp'
      || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setOpen(true);
    } else if (event.key === 'Escape' && open) {
      event.preventDefault();
      setOpen(false, true);
    }
  };

  const onMenuKey = (event: KeyboardEvent): void => {
    switch (event.key) {
      case 'ArrowDown': event.preventDefault(); focusOption(activeIndex() + 1); break;
      case 'ArrowUp': event.preventDefault(); focusOption(activeIndex() - 1); break;
      case 'Home': event.preventDefault(); focusOption(0); break;
      case 'End': event.preventDefault(); focusOption(options.length - 1); break;
      case 'Escape': event.preventDefault(); setOpen(false, true); break;
      case 'Tab': setOpen(false); break;
      case 'Enter':
      case ' ': {
        event.preventDefault();
        const id = (event.target as HTMLElement | null)?.dataset?.locale;
        if (id) choose(id as LocaleId);
        break;
      }
      default: break;
    }
  };

  const onMenuClick = (event: Event): void => {
    const id = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-locale]')
      ?.dataset.locale;
    if (id) choose(id as LocaleId);
  };

  // Outside dismissal on `pointerdown` rather than `click`: a touch that starts outside the
  // menu is already a dismissal, and waiting for the click lets the tap land on what is under
  // the menu as well.
  const onDocPointerDown = (event: Event): void => {
    if (!open) return;
    if (!root.contains(event.target as Node)) setOpen(false);
  };

  button.addEventListener('click', onButtonClick);
  button.addEventListener('keydown', onButtonKey);
  menu.addEventListener('keydown', onMenuKey);
  menu.addEventListener('click', onMenuClick);
  document.addEventListener('pointerdown', onDocPointerDown, true);

  const unsubscribe = subscribe(() => {
    paint();
    button.setAttribute('aria-label', t('common.chooseLanguage'));
    menu.setAttribute('aria-label', t('common.language'));
  });

  paint();

  return () => {
    unsubscribe();
    document.removeEventListener('pointerdown', onDocPointerDown, true);
    button.removeEventListener('click', onButtonClick);
    button.removeEventListener('keydown', onButtonKey);
    menu.removeEventListener('keydown', onMenuKey);
    menu.removeEventListener('click', onMenuClick);
    root.remove();
  };
}
